import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Lock, Check, Star, Sparkles, Crown, Zap } from "lucide-react";
import Navbar from "../components/Navbar";
import SkinPreview from "../components/SkinPreview";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Season Pass — monthly cosmetic ladder.
 *  - Free track + Premium track ($4.99/mo)
 *  - 30 tiers; 500 XP per tier
 *  - XP earned passively via gameplay (level clears, daily runs, bosses)
 *  - Each tier shows the free reward + the premium reward, with claim
 *    buttons gated on (a) tier reached and (b) premium ownership.
 */
export default function Season() {
  const [state, setState] = useState(null);
  // Catalog of all ball skins, indexed by id. Lets us render the
  // SkinPreview component for any season-pass reward of type "skin"
  // by looking up the skin object from its id.
  const [skinsById, setSkinsById] = useState({});
  const [claiming, setClaiming] = useState({}); // {`${tier}-${track}`: bool}
  const [purchasing, setPurchasing] = useState(false);

  const refresh = async () => {
    try {
      const [seasonR, skinsR, prefR] = await Promise.all([
        axios.get(`${API}/season-pass`, { withCredentials: true }),
        axios.get(`${API}/ball-skins`, { withCredentials: true }),
        axios.get(`${API}/subscription-reminders/prefs`, { withCredentials: true }),
      ]);
      setState(seasonR.data);
      setRenewPref(!!prefR.data.auto_renew_reminder);
      const map = {};
      for (const s of skinsR.data.skins || []) map[s.id] = s;
      setSkinsById(map);
    } catch (err) {
      if (err?.response?.status === 401) {
        window.location.href = "/login";
      }
    }
  };

  const toggleRenewPref = async () => {
    const next = !renewPref;
    try {
      await axios.post(
        `${API}/subscription-reminders/prefs`,
        { auto_renew_reminder: next },
        { withCredentials: true }
      );
      setRenewPref(next);
      toast.success(
        next
          ? "Renewal reminders ON — we'll ping you on the 1st"
          : "Renewal reminders OFF",
        { duration: 3500 }
      );
    } catch {
      toast.error("Couldn't save preference");
    }
  };

  useEffect(() => {
    refresh();
    // Auto-claim premium unlock from Stripe redirect.
    const url = new URL(window.location.href);
    const sessionId = url.searchParams.get("session_id");
    if (sessionId && url.searchParams.get("product") === "season-pass") {
      (async () => {
        try {
          const r = await axios.get(
            `${API}/season-pass/checkout/status/${sessionId}`,
            { withCredentials: true }
          );
          if (r.data.payment_status === "paid") {
            toast.success("Season Pass Premium unlocked!", {
              description: "Premium tier rewards are now claimable.",
            });
            await refresh();
          }
        } catch {
          /* swallow */
        }
        url.searchParams.delete("session_id");
        url.searchParams.delete("product");
        window.history.replaceState({}, "", url.toString());
      })();
    }
  }, []);

  const claim = async (tier, track) => {
    const key = `${tier}-${track}`;
    if (claiming[key]) return;
    setClaiming({ ...claiming, [key]: true });
    try {
      const r = await axios.post(
        `${API}/season-pass/claim`,
        { tier, track },
        { withCredentials: true }
      );
      const skin = r.data.awarded_skin;
      const stones = r.data.awarded_moonstones;
      toast.success(
        skin
          ? `Tier ${tier} ${track} reward claimed`
          : `+${stones} ☾ from tier ${tier}`,
        {
          description: skin
            ? `Unlocked: ${skin.replace(/-/g, " ")} ball skin${stones > 0 ? ` · +${stones} ☾` : ""}`
            : `Wallet updated.`,
        }
      );
      await refresh();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Claim failed");
    } finally {
      setClaiming((p) => ({ ...p, [key]: false }));
    }
  };

  const buyPremium = async () => {
    if (purchasing) return;
    setPurchasing(true);
    try {
      const r = await axios.post(
        `${API}/season-pass/checkout`,
        { origin_url: window.location.origin },
        { withCredentials: true }
      );
      window.location.href = r.data.url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't start checkout");
      setPurchasing(false);
    }
  };

  if (!state) {
    return (
      <div className="relative min-h-screen">
        <div className="starfield" />
        <Navbar />
        <main className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 pt-24 pb-16">
          <div className="text-white/50 font-mono-cosmic text-xs tracking-[0.25em] uppercase">
            Loading…
          </div>
        </main>
      </div>
    );
  }

  const xpInTier = state.xp - state.tier * state.xp_per_tier;
  const pctInTier = Math.min(100, Math.round((xpInTier / state.xp_per_tier) * 100));

  return (
    <div className="relative min-h-screen">
      <div className="starfield" />
      <Navbar />
      <main
        className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 pt-24 pb-16 fade-in"
        data-testid="season-pass-page"
      >
        {/* Header */}
        <div className="mb-8">
          <div className="font-mono-cosmic text-[10px] tracking-[0.4em] text-[#ffd700]/70 uppercase mb-2">
            // SEASON {state.season_id}
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl tracking-tight text-white">
            Season Pass
          </h1>
          <p className="text-sm text-white/55 mt-2 max-w-xl">
            Earn XP from every level, daily run, and boss kill. Claim free
            track rewards as you tier up — or unlock the premium track for
            doubled moonstones and exclusive cosmetic ball skins.
          </p>
        </div>

        {/* Final-week 2× XP banner — only renders when xp_boost_active=true
            (last 7 days of the month). Pulses gold to telegraph urgency. */}
        {state.xp_boost_active && (
          <div
            className="mb-6 border border-[#ffd700] bg-gradient-to-r from-[#ffd700]/15 via-[#ff007f]/15 to-[#ffd700]/15 px-4 py-3 daily-login-pulse flex items-center gap-3"
            data-testid="season-xp-boost-banner"
          >
            <Zap className="w-5 h-5 text-[#ffd700] shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="font-mono-cosmic text-[10px] tracking-[0.3em] text-[#ffd700] uppercase">
                FINAL WEEK · {state.xp_boost_multiplier}× XP ACTIVE
              </div>
              <div className="text-xs sm:text-sm text-white/85 mt-0.5">
                {state.days_left_in_season} day{state.days_left_in_season === 1 ? "" : "s"} left in
                season {state.season_id} · every level you clear earns{" "}
                <span className="text-[#ffd700] font-semibold">double XP</span>.
              </div>
            </div>
          </div>
        )}

        {/* Status row */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-10">
          <div
            className="border border-white/15 bg-black/30 p-4"
            data-testid="season-tier-badge"
          >
            <div className="font-mono-cosmic text-[9px] tracking-[0.3em] text-white/50 uppercase mb-1">
              CURRENT TIER
            </div>
            <div className="font-heading text-3xl text-white tabular-nums">
              {state.tier} <span className="text-base text-white/40">/ {state.tier_count}</span>
            </div>
            <div className="mt-2 h-1.5 bg-white/10 relative overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-[#3aa8ff] to-[#ff007f]"
                style={{ width: `${pctInTier}%` }}
                data-testid="season-tier-progress"
              />
            </div>
            <div className="font-mono-cosmic text-[9px] tracking-[0.2em] text-white/45 mt-1.5 tabular-nums">
              {xpInTier} / {state.xp_per_tier} XP TO NEXT
            </div>
          </div>

          <div className="border border-white/15 bg-black/30 p-4">
            <div className="font-mono-cosmic text-[9px] tracking-[0.3em] text-white/50 uppercase mb-1">
              TOTAL XP
            </div>
            <div className="font-heading text-3xl text-white tabular-nums">
              {state.xp.toLocaleString()}
            </div>
            <div className="font-mono-cosmic text-[9px] tracking-[0.2em] text-white/45 mt-3.5">
              +50 LEVEL · +50 PERFECT · +200 BOSS · +100 DAILY
            </div>
          </div>

          {/* Premium status — either purchased badge or buy CTA */}
          {state.premium ? (
            <div
              className="border border-[#ffd700]/60 bg-gradient-to-br from-[#ffd700]/10 to-[#ff007f]/10 p-4"
              data-testid="season-premium-active"
            >
              <div className="flex items-center gap-2 mb-1">
                <Crown className="w-3.5 h-3.5 text-[#ffd700]" />
                <div className="font-mono-cosmic text-[9px] tracking-[0.3em] text-[#ffd700] uppercase">
                  PREMIUM ACTIVE
                </div>
              </div>
              <div className="font-heading text-2xl text-[#ffd700]">Unlocked</div>
              <div className="text-[10px] text-white/55 mt-3">
                All premium tier rewards are claimable for season {state.season_id}.
              </div>
              {/* Renewal-reminder toggle — only meaningful for premium pilots
                  since the cron only pings pilots who had premium last month. */}
              <button
                onClick={toggleRenewPref}
                disabled={renewPref === null}
                className="mt-3 w-full py-2 border font-mono-cosmic text-[9px] tracking-[0.25em] uppercase transition flex items-center justify-between gap-2 px-2"
                style={{
                  borderColor: renewPref ? "#ffd700" : "rgba(255,255,255,0.2)",
                  color: renewPref ? "#ffd700" : "rgba(255,255,255,0.55)",
                  background: renewPref ? "rgba(255,215,0,0.08)" : "transparent",
                }}
                data-testid="season-renew-pref-toggle"
              >
                <span>RENEWAL REMINDER</span>
                <span className="tabular-nums">
                  {renewPref === null ? "…" : renewPref ? "ON" : "OFF"}
                </span>
              </button>
              <div className="text-[8px] text-white/35 font-mono-cosmic tracking-[0.15em] mt-1.5 leading-relaxed">
                Get a push notification on the 1st of each month to renew. Not auto-charged.
              </div>
            </div>
          ) : (
            <button
              onClick={buyPremium}
              disabled={purchasing}
              className="border border-[#ffd700]/60 bg-gradient-to-br from-[#ffd700]/10 to-[#ff007f]/10 p-4 text-left hover:from-[#ffd700]/20 hover:to-[#ff007f]/20 transition disabled:opacity-50"
              data-testid="season-buy-premium-btn"
            >
              <div className="flex items-center gap-2 mb-1">
                <Crown className="w-3.5 h-3.5 text-[#ffd700]" />
                <div className="font-mono-cosmic text-[9px] tracking-[0.3em] text-[#ffd700] uppercase">
                  UNLOCK PREMIUM
                </div>
              </div>
              <div className="font-heading text-2xl text-white">
                ${state.premium_price_usd}
              </div>
              <div className="text-[10px] text-white/55 mt-3">
                {purchasing ? "Redirecting to checkout…" : "2× moonstones + 4 exclusive cosmetic skins"}
              </div>
            </button>
          )}
        </div>

        {/* Tier ladder */}
        <div className="space-y-1.5" data-testid="season-track">
          {/* Header row */}
          <div className="grid grid-cols-[60px_minmax(0,1fr)_minmax(0,1fr)] gap-2 text-[9px] font-mono-cosmic tracking-[0.25em] uppercase text-white/40 px-2 pb-1 border-b border-white/10">
            <div>TIER</div>
            <div>FREE TRACK</div>
            <div className="flex items-center gap-1">
              <Crown className="w-3 h-3 text-[#ffd700]/70" />
              PREMIUM TRACK
            </div>
          </div>

          {state.track.map((row) => {
            const reached = row.tier <= state.tier;
            const claimedFree = state.claimed_free.includes(row.tier);
            const claimedPrem = state.claimed_premium.includes(row.tier);
            return (
              <div
                key={row.tier}
                className={`grid grid-cols-[60px_minmax(0,1fr)_minmax(0,1fr)] gap-2 items-stretch ${
                  reached ? "" : "opacity-60"
                }`}
                data-testid={`season-row-${row.tier}`}
              >
                {/* Tier number */}
                <div
                  className={`flex flex-col items-center justify-center py-2 border ${
                    reached
                      ? "border-[#ffd700]/50 bg-[#ffd700]/10"
                      : "border-white/10 bg-white/5"
                  }`}
                >
                  <div
                    className="font-heading text-lg tabular-nums"
                    style={{ color: reached ? "#ffd700" : "rgba(255,255,255,0.45)" }}
                  >
                    {row.tier}
                  </div>
                  <div className="font-mono-cosmic text-[8px] tracking-[0.15em] text-white/40">
                    {row.xp_required}xp
                  </div>
                </div>

                {/* Free track cell */}
                <RewardCell
                  reward={row.free}
                  skin={row.free.type === "skin" ? skinsById[row.free.skin_id] : null}
                  reached={reached}
                  claimed={claimedFree}
                  claiming={claiming[`${row.tier}-free`]}
                  onClaim={() => claim(row.tier, "free")}
                  testId={`season-free-${row.tier}`}
                />

                {/* Premium track cell */}
                <RewardCell
                  reward={row.premium}
                  skin={row.premium.type === "skin" ? skinsById[row.premium.skin_id] : null}
                  reached={reached}
                  claimed={claimedPrem}
                  claiming={claiming[`${row.tier}-premium`]}
                  onClaim={() => claim(row.tier, "premium")}
                  testId={`season-prem-${row.tier}`}
                  premiumLocked={!state.premium}
                  isPremium
                />
              </div>
            );
          })}
        </div>
      </main>
    </div>
  );
}

function RewardCell({
  reward,
  skin,
  reached,
  claimed,
  claiming,
  onClaim,
  testId,
  premiumLocked = false,
  isPremium = false,
}) {
  const isSkin = reward.type === "skin";
  const stones = isSkin ? reward.moonstones || 0 : reward.amount || 0;
  const accent = isPremium ? "#ffd700" : "#3aa8ff";
  const canClaim = reached && !claimed && !claiming && !(isPremium && premiumLocked);

  return (
    <div
      className={`border p-2 flex items-center gap-2 ${
        claimed
          ? "border-white/10 bg-white/5"
          : reached
            ? `border-${isPremium ? "yellow" : "white"}/30 bg-black/30`
            : "border-white/10 bg-black/20"
      }`}
      style={reached && !claimed ? { borderColor: `${accent}55` } : undefined}
      data-testid={testId}
    >
      {/* Reward visual — animated SkinPreview for skin rewards, static
          icon for moonstone-only rewards. The 36×36 square preview keeps
          the row compact while still showing the skin's flame in motion. */}
      <div className="shrink-0">
        {isSkin && skin ? (
          <div className="w-9 h-9 overflow-hidden bg-black/60 border border-white/10">
            <SkinPreview skin={skin} size={36} aspect="square" />
          </div>
        ) : isSkin ? (
          <Sparkles className="w-4 h-4" style={{ color: accent }} />
        ) : (
          <Star className="w-4 h-4" style={{ color: accent }} />
        )}
      </div>
      {/* Reward label */}
      <div className="flex-1 min-w-0">
        <div className="font-mono-cosmic text-[10px] tracking-[0.15em] text-white truncate uppercase">
          {isSkin
            ? (skin?.name || (reward.skin_id || "").replace(/-/g, " "))
            : `+${stones} MOONSTONES`}
        </div>
        {isSkin && stones > 0 && (
          <div className="font-mono-cosmic text-[8px] tracking-[0.15em] text-[#ffd700]/70 uppercase">
            +{stones} ☾ bonus
          </div>
        )}
      </div>
      {/* CTA */}
      {claimed ? (
        <div
          className="text-[9px] font-mono-cosmic tracking-[0.2em] uppercase flex items-center gap-1"
          style={{ color: "rgba(255,255,255,0.45)" }}
        >
          <Check className="w-3 h-3" /> CLAIMED
        </div>
      ) : isPremium && premiumLocked ? (
        <div className="text-[9px] font-mono-cosmic tracking-[0.2em] uppercase text-[#ffd700]/60 flex items-center gap-1">
          <Lock className="w-3 h-3" /> PREMIUM
        </div>
      ) : !reached ? (
        <div className="text-[9px] font-mono-cosmic tracking-[0.2em] uppercase text-white/35 flex items-center gap-1">
          <Lock className="w-3 h-3" /> LOCKED
        </div>
      ) : (
        <button
          onClick={onClaim}
          disabled={!canClaim}
          className="px-2 py-1 border text-[9px] font-mono-cosmic tracking-[0.2em] uppercase transition disabled:opacity-40"
          style={{
            borderColor: accent,
            color: accent,
            background: `${accent}15`,
          }}
          data-testid={`${testId}-claim-btn`}
        >
          {claiming ? "…" : "CLAIM"}
        </button>
      )}
    </div>
  );
}
